import { userHasRole as userHasRoleHelper, isClientUser } from '../app/router/helpers';

/**
 * Shared navigation item definitions used by both the hamburger menu and New Tab page.
 * Single source of truth for which items each role can access.
 *
 * @param {Object} options
 * @param {Object} options.user - Current user object from AuthContext
 * @param {Function} options.hasPermission - Permission check from usePermissions hook
 * @param {boolean} [options.loading] - Whether permissions are still loading
 * @param {Object} [options.onboardingStatus] - Onboarding status for admins (optional)
 * @returns {Array} Menu items the user is allowed to see
 */
export function getNavigationItems({ user, hasPermission, loading, onboardingStatus, providerOnboardingCompleted, onOpenClaimDialog }: {
  user: Record<string, unknown> | false | null;
  hasPermission: (perm: string) => boolean;
  loading?: boolean;
  onboardingStatus?: { readiness_score: number } | null;
  providerOnboardingCompleted?: boolean | null;
  /** Callback to open the ClaimOrgDialog from the menu (demo mode). */
  onOpenClaimDialog?: () => void;
}) {
  if (!user) {
    return [
      { label: 'Dashboard', route: '/dashboard', description: 'Overview and quick actions' },
      { label: 'Settings', route: '/settings', description: 'Update profile and preferences' },
      { label: 'Logout', route: 'logout', description: 'Sign out of your account' }
    ];
  }

  const userHasRole = (role) => userHasRoleHelper(user, role);

  // Platform owner gets a completely separate menu — no therapist/client items
  // Routes use ?section=<key> matching the sub-page keys in PlatformOwnerDashboard
  if (userHasRole('platform_owner')) {
    return [
      { type: 'section', label: 'Platform', key: 'platform', icon: '\uD83C\uDFE2', collapsible: false },
      { label: 'Dashboard', route: '/platform-owner', description: 'Platform metrics overview' },
      { label: 'Organizations', route: '/platform-owner?section=organizations', description: 'Manage all organizations' },
      { label: 'Users', route: '/platform-owner?section=users', description: 'Manage all users' },
      { type: 'section', label: 'Operations', key: 'po-operations', icon: '\u2699\uFE0F', collapsible: true },
      { label: 'Subscriptions', route: '/platform-owner?section=subscriptions', description: 'Subscription management', parentSection: 'po-operations' },
      { label: 'Trial Codes', route: '/platform-owner?section=trial-codes', description: 'Manage trial codes', parentSection: 'po-operations' },
      { label: 'Feature Flags', route: '/platform-owner?section=feature-flags', description: 'Toggle feature flags', parentSection: 'po-operations' },
      { label: 'Pricing', route: '/platform-owner?section=pricing', description: 'Pricing configuration', parentSection: 'po-operations' },
      { type: 'section', label: 'Monitoring', key: 'po-monitoring', icon: '\uD83D\uDCCA', collapsible: true },
      { label: 'Health', route: '/platform-owner?section=health', description: 'System health status', parentSection: 'po-monitoring' },
      { label: 'Uptime', route: '/platform-owner?section=uptime', description: 'Uptime monitoring', parentSection: 'po-monitoring' },
      { label: 'Sentry', route: '/platform-owner?section=sentry', description: 'Error tracking', parentSection: 'po-monitoring' },
      { label: 'Audit Log', route: '/platform-owner?section=audit-log', description: 'System audit trail', parentSection: 'po-monitoring' },
      { label: 'Config', route: '/platform-owner?section=config', description: 'Environment configuration', parentSection: 'po-monitoring' },
      { type: 'section', label: 'Account', key: 'po-account', icon: '\uD83D\uDC64', collapsible: true },
      { label: 'My Settings', route: '/my-settings', description: 'Profile and preferences', parentSection: 'po-account' },
      { label: 'Logout', route: 'logout', description: 'Sign out', parentSection: 'po-account' },
    ];
  }

  const isClient = isClientUser(user);

  if (isClient) {
    const clientMenuItems = [];

    // Client menu items link into the client portal tabs (/client?tab=X) rather
    // than standalone /client/* pages so there is a single source of truth for
    // each portal feature (avoids duplicate/diverging pages and provider-only
    // pages leaking into the client experience).
    clientMenuItems.push({ type: 'section', label: 'My Care', key: 'my-care' });
    clientMenuItems.push({ label: 'Home', route: '/client?tab=dashboard', description: 'Overview and quick actions' });
    clientMenuItems.push({ label: 'Appointments', route: '/client?tab=appointments', permission: 'scheduling.view', description: 'View upcoming sessions' });
    clientMenuItems.push({ label: 'Messages', route: '/client?tab=messages', permission: 'messaging.view', description: 'Message your care team' });
    clientMenuItems.push({ label: 'Billing', route: '/client?tab=payments', permission: 'billing.view', description: 'View bills and payments' });
    clientMenuItems.push({ label: 'Medications', route: '/client?tab=medications', description: 'Review current medications' });
    clientMenuItems.push({ label: 'My Tracker', route: '/client?tab=health', permission: 'health_tracking.view', description: 'Track mood, sleep, and trends' });
    clientMenuItems.push({ label: 'Resources', route: '/client?tab=resources', permission: 'documents.view', description: 'Homework, articles, and resources' });

    clientMenuItems.push({ type: 'section', label: 'Administrative', key: 'administrative' });
    clientMenuItems.push({ label: 'Getting Started', route: '/client/getting-started', description: 'Your onboarding guide and next steps' });
    clientMenuItems.push({ label: 'Paperwork & Consents', route: '/client?tab=paperwork', description: 'Intake forms, consent, and signatures' });
    clientMenuItems.push({ label: 'My Information', route: '/client?tab=settings', description: 'Contact information and preferences' });
    clientMenuItems.push({ label: 'Logout', route: 'logout', description: 'Sign out of your account' });

    return clientMenuItems.filter(item => !item.permission || hasPermission(item.permission));
  }

  if (loading) {
    return [
      { label: 'Dashboard', route: '/dashboard', description: 'Overview and quick actions' },
      { label: 'Settings', route: '/settings', description: 'Update profile and preferences' },
      { label: 'Notifications', route: '/notifications', description: 'View alerts and updates' },
      { label: 'Logout', route: 'logout', description: 'Sign out of your account' }
    ];
  }

  const menuItems = [];
  const hasAdminPerms = hasPermission('settings.users');
  const hasIntakeCoordinator = hasPermission('leads.view');
  const isSupervisor = userHasRole('supervisor');

  // Onboarding CTAs — shown at top of menu based on setup progress.
  // Gate is setup completion (readiness_score), NOT subscription status.
  const hasClaimed = user.claimed_org === true;
  const clinicName = typeof user.clinic_name === 'string' ? user.clinic_name : '';
  const setupComplete = onboardingStatus && onboardingStatus.readiness_score >= 100;

  if (!hasClaimed && onOpenClaimDialog) {
    // No clinic yet: encourage naming (opens ClaimOrgDialog)
    menuItems.push({
      label: 'Name Your Clinic',
      description: 'Start customizing with your website',
      highlight: true,
      action: onOpenClaimDialog,
    });
    menuItems.push({ type: 'divider' });
  } else if (hasClaimed && hasAdminPerms && !setupComplete) {
    // Clinic named but setup steps remain — admin-only since route requires admin perms.
    menuItems.push({
      label: clinicName ? `Continue Customizing ${clinicName}` : 'Continue Customizing Your Clinic',
      route: '/admin/setup-wizard',
      description: 'Pick up where you left off',
      highlight: true,
    });
    menuItems.push({ type: 'divider' });
  }

  // NOTE: The old "Finish Setup" CTA (/admin/onboarding) is intentionally
  // removed — "Continue Customizing" above now serves as the single setup
  // entry point for all users, pointing to the setup wizard.

  // DAILY
  menuItems.push({ type: 'section', label: 'Daily', key: 'daily', icon: '\u2600\uFE0F', collapsible: false });
  menuItems.push({ label: 'To-Do', route: '/tasks', description: 'Documentation tasks' });
  if (hasPermission('scheduling.view')) {
    menuItems.push({ label: 'Calendar', route: '/calendar', description: 'View and manage your schedule' });
  }
  if (hasPermission('notes.create')) {
    menuItems.push({ label: 'Drive Mode', route: '/drive-mode', description: 'Hands-free note writing' });
  }
  if (hasPermission('messaging.view')) {
    menuItems.push({ label: 'Messages', route: '/messages', description: 'Secure messaging' });
  }
  if (hasPermission('timesheets.view_self')) {
    menuItems.push({ label: 'Timesheets', route: '/my-work', description: 'Timesheet, payroll, analytics' });
  }
  if (hasIntakeCoordinator) {
    menuItems.push({ label: 'Leads', route: '/leads', description: 'Manage prospective clients' });
  }

  // CLINICAL
  menuItems.push({ type: 'section', label: 'Clinical', key: 'clinical', icon: '\uD83E\uDE7A', collapsible: true });

  menuItems.push({ type: 'subsection', label: 'Clinical Work', key: 'clinical-work', parentSection: 'clinical' });
  if (hasPermission('notes.view')) {
    menuItems.push({ label: 'Notes', route: '/notes', description: 'Note list and creation', parentSection: 'clinical' });
  }
  if (hasPermission('patients.view')) {
    menuItems.push({ label: 'Outcomes', route: '/outcomes', description: 'Track progress across clients', parentSection: 'clinical' });
  }
  if (hasPermission('patients.view')) {
    menuItems.push({ label: 'Caseload', route: '/cases', description: 'Your active cases — individuals, couples, and families', parentSection: 'clinical' });
    menuItems.push({ label: 'All Person Records', route: '/client-records', description: 'Organization-wide people directory', parentSection: 'clinical' });
  }
  if (hasPermission('billing.view')) {
    menuItems.push({ label: 'Billing', route: '/billing', description: 'Billing dashboard', parentSection: 'clinical' });
  }
  if (hasPermission('patients.view')) {
    menuItems.push({ label: 'Case Metrics', route: '/my-caseload', description: 'Your cases with progress metrics and notes', parentSection: 'clinical' });
  }

  if (isSupervisor) {
    menuItems.push({ type: 'subsection', label: 'Supervision', key: 'supervision', parentSection: 'clinical' });
    menuItems.push({ label: 'Supervision Queue', route: '/supervision', description: 'Review and co-sign notes', parentSection: 'clinical' });
    menuItems.push({ label: 'Supervisee Caseload', route: '/supervision/caseload', description: 'Caseload overview with metrics and narratives', parentSection: 'clinical' });
    if (hasPermission('reports.view')) {
      menuItems.push({ label: 'Supervisee Outcomes', route: '/admin/outcomes', description: 'Supervisee outcomes analytics', parentSection: 'clinical' });
    }
  }

  // WORKSPACE
  menuItems.push({ type: 'section', label: 'Workspace', key: 'workspace', icon: '\u2728', collapsible: true });
  if (hasPermission('notes.create') || hasAdminPerms) {
    menuItems.push({ label: 'AI Chat', route: '/ai-assistant', description: 'AI assistant for clinical support', parentSection: 'workspace' });
  }
  menuItems.push({ label: 'Projects', route: '/projects', description: 'Create documents with or without AI help', parentSection: 'workspace' });
  menuItems.push({ label: 'Library', route: '/library', description: 'Store, organize, and share clinical resources', parentSection: 'workspace' });
  if (hasPermission('reflections.view')) {
    menuItems.push({ label: 'Reflections', route: '/reflections', description: 'Private therapist reflections', parentSection: 'workspace' });
  }

  // REPORTS (anyone with reports.view — admins, clinical directors, etc.)
  if (hasPermission('reports.view')) {
    menuItems.push({ type: 'section', label: 'Reports', key: 'oversight', icon: '\uD83D\uDCCA', collapsible: true });
    menuItems.push({ type: 'subsection', label: 'Money & Productivity', key: 'money', parentSection: 'oversight' });
    menuItems.push({ label: 'Practice Health', route: '/admin/practice-health', description: 'Owner view of practice growth, capacity, and financial health', parentSection: 'oversight' });
    menuItems.push({ label: 'Analytics', route: '/analytics', description: 'Practice analytics & reports', parentSection: 'oversight' });
    if (hasPermission('payroll.manage')) {
      menuItems.push({ label: 'Payroll', route: '/admin/payroll', description: 'Payroll management', parentSection: 'oversight' });
    }
  }

  // CLINIC SETTINGS (admins only)
  if (hasAdminPerms) {
    menuItems.push({ type: 'section', label: 'Clinic Settings', key: 'system', icon: '\uD83D\uDEE0', collapsible: true });
    menuItems.push({ label: 'Admin Settings', route: '/admin/settings', description: 'Practice configuration', parentSection: 'system' });
    menuItems.push({ label: 'Staff / Roles', route: '/admin/staff', description: 'User management', parentSection: 'system' });
    menuItems.push({ label: 'Client Paperwork', route: '/admin/forms', description: 'Form templates and paperwork bundles', parentSection: 'system' });
    menuItems.push({ label: 'Data Migration', route: '/admin/migration', description: 'Import/export data', parentSection: 'system' });
    menuItems.push({ label: 'Import History', route: '/admin/migration/history', description: 'Review prior imports and recover safely', parentSection: 'system' });
  }

  // COMPLIANCE (admins only)
  if (hasAdminPerms) {
    menuItems.push({ type: 'section', label: 'Compliance', key: 'operations', icon: '\u2699\uFE0F', collapsible: true });
    menuItems.push({ type: 'subsection', label: 'Monitoring & Review', key: 'monitoring', parentSection: 'operations' });
    menuItems.push({ label: 'Audit Logs', route: '/admin/audit-logs', description: 'System audit trail', parentSection: 'operations' });
    menuItems.push({ label: 'Support', route: '/admin/support', description: 'Delivery, payment and AI failures; request tracing', parentSection: 'operations' });
    menuItems.push({ label: 'Data Merges', route: '/admin/merges', description: 'Merge duplicate patient or practitioner records', parentSection: 'operations' });
    menuItems.push({ label: 'HIPAA Compliance', route: '/admin/compliance', description: 'Compliance dashboard', parentSection: 'operations' });
  }

  // MY ACCOUNT (at bottom)
  menuItems.push({ type: 'section', label: 'My Account', key: 'my-account', icon: '\uD83D\uDC64', collapsible: true });
  // Provider onboarding wizard link — visible only when incomplete
  if (providerOnboardingCompleted === false) {
    menuItems.push({
      label: 'Provider Setup Wizard',
      route: '/my-settings?tab=profile&wizard=1',
      description: 'Complete your provider onboarding',
      parentSection: 'my-account',
      highlight: true,
    });
  }
  menuItems.push({ label: 'Getting Started', route: '/getting-started', description: 'Onboarding guide and next steps', parentSection: 'my-account' });
  menuItems.push({ label: 'My Settings', route: '/my-settings', description: 'Profile, availability, schedule', parentSection: 'my-account' });

  return menuItems.filter(item => !item.permission || hasPermission(item.permission));
}

/**
 * Filter navigation items to only navigable entries (no section headers, dividers, subsections).
 * Excludes logout entries since those aren't navigable pages.
 */
export function getNavigableTiles(items) {
  return items.filter(item => !item.type && item.route && item.route !== 'logout');
}
